import {
  PDFDocument, PDFName, PDFBool, PDFArray, PDFDict, PDFRef, rgb, StandardFonts, degrees,
  PDFTextField, PDFCheckBox, PDFDropdown, PDFOptionList, PDFRadioGroup,
} from 'pdf-lib';
import { copyPageOverlays, finishPageAssembly } from './pageAssembly';
import { flattenAppearances, ensureFlattenAppearances, removeUnreachableObjects } from './pdfAppearanceFlatten';
import { persistOverlaysToDoc } from './overlayPersistence';
import { editToSegments } from './richText';
import { prepareTextRemoval, removalTargets, removePdfContent, hasDigitalSignature } from './pdfContentRemoval';

/**
 * Resolve our internal font-family + bold/italic to a pdf-lib font key (one
 * of the 14 Standard fonts). Used both by renderTextEdit and by the
 * bakedBounds whiteout, which needs the same font to measure the glyph
 * width of restored text.
 *
 * 'Arial' collapses to Helvetica deliberately, not as a fallback: the two are
 * metrically compatible (identical advance widths per glyph), so Helvetica's
 * width table measures Arial text exactly, and every PDF viewer already
 * substitutes Helvetica for non-embedded Arial. That keeps Arial a Standard-14
 * font — no embedded font file, no licensing exposure, no bundle cost — while
 * MaskLayer's canvas measurement (which sees the real Arial on the user's OS)
 * still agrees with what pdf-lib bakes.
 */
export function resolveFontKey(segStyles) {
  let family = segStyles?.fontFamily || 'Helvetica';
  if (['Lato', 'Roboto', 'Inter', 'Carlito', 'Arial'].includes(family)) family = 'Helvetica';
  if (family === 'Roboto Slab') family = 'Times-Roman';
  if (segStyles?.bold && segStyles?.italic) {
    if (family === 'Times-Roman') return 'Times-BoldItalic';
    if (family === 'Courier') return 'Courier-BoldOblique';
    return 'Helvetica-BoldOblique';
  }
  if (segStyles?.bold) {
    if (family === 'Times-Roman') return 'Times-Bold';
    if (family === 'Courier') return 'Courier-Bold';
    return 'Helvetica-Bold';
  }
  if (segStyles?.italic) {
    if (family === 'Times-Roman') return 'Times-Italic';
    if (family === 'Courier') return 'Courier-Oblique';
    return 'Helvetica-Oblique';
  }
  return family;
}

/**
 * pdfOperations — Core PDF manipulation functions.
 * Uses pdf-lib for all structural operations (merge, split, compress, etc.).
 * PDF.js is only used for rendering — pdf-lib handles the actual file work.
 */

/**
 * Merge multiple PDFs into one.
 *
 * @param {Array<{ arrayBuffer: ArrayBuffer, selectedPages: Set<number> }>} sources
 *   Each source has the raw file bytes and a set of 1-indexed page numbers to include.
 * @param {(progress: number) => void} onProgress
 *   Called with a value from 0 to 1 as pages are copied.
 * @returns {Promise<Uint8Array>} The merged PDF bytes.
 */
export async function mergePDFs(sources, onProgress = () => {}) {
  const merged = await PDFDocument.create();

  // Count total pages for progress tracking
  let totalPages = 0;
  for (const source of sources) {
    totalPages += source.selectedPages.size;
  }

  let copiedPages = 0;
  const overlays = { edits: [], annotations: [] };

  for (const source of sources) {
    const srcDoc = await PDFDocument.load(source.arrayBuffer);
    // Convert 1-indexed selected pages to 0-indexed for pdf-lib
    const pageIndices = Array.from(source.selectedPages)
      .sort((a, b) => a - b)
      .map((p) => p - 1);

    const copiedOverlays = await copyPageOverlays(srcDoc, pageIndices.map((i) => i + 1), copiedPages, `merge-${copiedPages}`);
    overlays.edits.push(...copiedOverlays.edits);
    overlays.annotations.push(...copiedOverlays.annotations);
    const pages = await merged.copyPages(srcDoc, pageIndices);
    for (const page of pages) {
      merged.addPage(page);
      copiedPages++;
      onProgress(totalPages > 0 ? copiedPages / totalPages : 1);
    }
  }

  return finishPageAssembly(merged, overlays);
}

/**
 * Extract specific pages from a PDF into a new document.
 *
 * @param {ArrayBuffer} arrayBuffer — The source PDF bytes.
 * @param {number[]} pageNumbers — 1-indexed page numbers to include.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>} The new PDF bytes.
 */
export async function splitPDF(arrayBuffer, pageNumbers, onProgress = () => {}) {
  const srcDoc = await PDFDocument.load(arrayBuffer);
  const newDoc = await PDFDocument.create();

  // Preserve the caller's order (important for reordered extract)
  const indices = pageNumbers.map((p) => p - 1);

  const pages = await newDoc.copyPages(srcDoc, indices);
  for (let i = 0; i < pages.length; i++) {
    newDoc.addPage(pages[i]);
    onProgress(pages.length > 0 ? (i + 1) / pages.length : 1);
  }

  return finishPageAssembly(newDoc, await copyPageOverlays(srcDoc, pageNumbers, 0, 'extract'));
}

/**
 * Extract each page as a separate single-page PDF.
 * Loads the source once for efficiency.
 *
 * @param {ArrayBuffer} arrayBuffer — The source PDF bytes.
 * @param {number[]} pageNumbers — 1-indexed page numbers to extract.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Array<{ pageNum: number, bytes: Uint8Array }>>}
 */
export async function extractSeparatePDFs(arrayBuffer, pageNumbers, onProgress = () => {}) {
  const srcDoc = await PDFDocument.load(arrayBuffer);
  const results = [];

  for (let i = 0; i < pageNumbers.length; i++) {
    const newDoc = await PDFDocument.create();
    const [page] = await newDoc.copyPages(srcDoc, [pageNumbers[i] - 1]);
    newDoc.addPage(page);
    const bytes = await finishPageAssembly(newDoc, await copyPageOverlays(srcDoc, [pageNumbers[i]], 0, 'extract'));
    results.push({ pageNum: pageNumbers[i], bytes });
    onProgress((i + 1) / pageNumbers.length);
  }

  return results;
}

/**
 * Split a PDF into multiple files by page groups.
 * Each group becomes a separate PDF.
 *
 * @param {ArrayBuffer} arrayBuffer — The source PDF bytes.
 * @param {number[][]} groups — Array of groups, each group is an array of 1-indexed page numbers.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Array<{ groupIndex: number, pageNumbers: number[], bytes: Uint8Array }>>}
 */
export async function splitPDFByGroups(arrayBuffer, groups, onProgress = () => {}) {
  const srcDoc = await PDFDocument.load(arrayBuffer);
  const results = [];

  const totalPages = groups.reduce((sum, g) => sum + g.length, 0);
  let copiedPages = 0;

  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi];
    const newDoc = await PDFDocument.create();
    const indices = group.map((p) => p - 1);
    const pages = await newDoc.copyPages(srcDoc, indices);
    for (const page of pages) {
      newDoc.addPage(page);
      copiedPages++;
      onProgress(totalPages > 0 ? copiedPages / totalPages : 1);
    }
    const bytes = await finishPageAssembly(newDoc, await copyPageOverlays(srcDoc, group, 0, `group-${gi}`));
    results.push({ groupIndex: gi, pageNumbers: group, bytes });
  }

  return results;
}

/**
 * Parse a range string like "1-3, 5, 8-12" into an array of page numbers.
 * Returns sorted, deduplicated, clamped to [1, maxPage].
 */
export function parsePageRange(rangeStr, maxPage) {
  const pages = new Set();
  const parts = rangeStr.split(',');

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const rangeParts = trimmed.split('-').map((s) => parseInt(s.trim(), 10));

    if (rangeParts.length === 1 && !isNaN(rangeParts[0])) {
      const p = rangeParts[0];
      if (p >= 1 && p <= maxPage) pages.add(p);
    } else if (rangeParts.length === 2 && !isNaN(rangeParts[0]) && !isNaN(rangeParts[1])) {
      const start = Math.max(1, rangeParts[0]);
      const end = Math.min(maxPage, rangeParts[1]);
      for (let i = start; i <= end; i++) pages.add(i);
    }
  }

  return Array.from(pages).sort((a, b) => a - b);
}

/**
 * Rotate specific pages in a PDF.
 *
 * @param {ArrayBuffer} arrayBuffer — The source PDF bytes.
 * @param {Map<number, number>} rotations — Map of 1-indexed page number → rotation degrees
 *   (90, 180, 270, or -90, -180, -270). Rotation is applied relative to the page's current rotation.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>} The modified PDF bytes.
 */
export async function rotatePDF(arrayBuffer, rotations, onProgress = () => {}) {
  const doc = await PDFDocument.load(arrayBuffer);
  const pages = doc.getPages();

  let processed = 0;
  const total = rotations.size;

  for (const [pageNum, addedAngle] of rotations) {
    const page = pages[pageNum - 1];
    if (!page) continue;
    const currentAngle = page.getRotation().angle;
    const finalAngle = ((currentAngle + addedAngle) % 360 + 360) % 360;
    page.setRotation(degrees(finalAngle));
    processed++;
    onProgress(total > 0 ? processed / total : 1);
  }

  return doc.save();
}

/**
 * Compress a PDF — low level (strip metadata, re-save).
 * Works well for PDFs with bloated metadata or unused objects.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>}
 */
export async function compressPDFLow(arrayBuffer, onProgress = () => {}) {
  const doc = await PDFDocument.load(arrayBuffer);

  // Repack structure without deleting document metadata or editable data.
  onProgress(0.5);

  const bytes = await doc.save({ useObjectStreams: true });
  onProgress(1);

  // Never return a larger file
  if (bytes.length >= arrayBuffer.byteLength) {
    return new Uint8Array(arrayBuffer);
  }
  return bytes;
}

/**
 * Compress a PDF — medium/high level.
 * Rebuilds the PDF from pre-rendered JPEG images of each page.
 * The caller is responsible for rendering pages (via PDF.js) and
 * encoding them as JPEG at the desired DPI and quality.
 *
 * If the rebuilt PDF is larger than the original, returns the original unchanged.
 *
 * @param {Array<{ pdfWidth: number, pdfHeight: number, jpegBytes: Uint8Array }>} pageImages
 * @param {ArrayBuffer} originalBuffer — The original file, used for size comparison.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>}
 */
export async function compressPDFFromImages(pageImages, originalBuffer, onProgress = () => {}) {
  const doc = await PDFDocument.create();

  for (let i = 0; i < pageImages.length; i++) {
    const { pdfWidth, pdfHeight, jpegBytes } = pageImages[i];
    const image = await doc.embedJpg(jpegBytes);
    const page = doc.addPage([pdfWidth, pdfHeight]);
    page.drawImage(image, { x: 0, y: 0, width: pdfWidth, height: pdfHeight });
    onProgress((i + 1) / pageImages.length);
  }

  const bytes = await doc.save({ useObjectStreams: true });

  // Never return a larger file
  if (bytes.length >= originalBuffer.byteLength) {
    return new Uint8Array(originalBuffer);
  }
  return bytes;
}

/**
 * Check if a PDF is encrypted.
 * Uses PDF.js which supports more encryption algorithms than pdf-lib.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {object} pdfjsLib — the PDF.js library (passed in to avoid circular imports)
 * @returns {Promise<boolean>}
 */
export async function isPDFEncrypted(arrayBuffer, pdfjsLib) {
  try {
    const doc = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
    doc.destroy();
    return false;
  } catch (err) {
    const msg = err.message || '';
    if (
      msg.includes('password') ||
      msg.includes('Password') ||
      msg.includes('encrypted') ||
      err.name === 'PasswordException'
    ) {
      return true;
    }
    throw err;
  }
}

/**
 * Unlock (decrypt) a password-protected PDF.
 *
 * Uses PDF.js to decrypt (it supports AES-256 and other modern algorithms
 * that pdf-lib doesn't handle). Renders each page to a JPEG image, then
 * rebuilds the PDF from those images using pdf-lib.
 *
 * Trade-off: the output is rasterized (text is no longer selectable).
 * This is noted in the UI. For lossless decryption, use the desktop app.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {string} password
 * @param {object} pdfjsLib
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>}
 */
export async function unlockPDF(arrayBuffer, password, pdfjsLib, onProgress = () => {}) {
  onProgress(0.05);

  // Load with PDF.js using the password
  const pdfDoc = await pdfjsLib.getDocument({
    data: arrayBuffer.slice(0),
    password,
  }).promise;

  onProgress(0.1);

  const numPages = pdfDoc.numPages;
  const pageImages = [];

  // Render each page to JPEG
  for (let i = 1; i <= numPages; i++) {
    const page = await pdfDoc.getPage(i);
    const origViewport = page.getViewport({ scale: 1 });
    // Render at 2x for good quality
    const renderViewport = page.getViewport({ scale: 2 });

    const canvas = document.createElement('canvas');
    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    const ctx = canvas.getContext('2d');

    await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    );
    const jpegBytes = new Uint8Array(await blob.arrayBuffer());

    pageImages.push({
      pdfWidth: origViewport.width,
      pdfHeight: origViewport.height,
      jpegBytes,
    });

    onProgress(0.1 + (i / numPages) * 0.7);
  }

  pdfDoc.destroy();

  // Rebuild as unencrypted PDF
  onProgress(0.85);
  const newDoc = await PDFDocument.create();

  for (const { pdfWidth, pdfHeight, jpegBytes } of pageImages) {
    const image = await newDoc.embedJpg(jpegBytes);
    const page = newDoc.addPage([pdfWidth, pdfHeight]);
    page.drawImage(image, { x: 0, y: 0, width: pdfWidth, height: pdfHeight });
  }

  const bytes = await newDoc.save({ useObjectStreams: true });
  onProgress(1);
  return bytes;
}

function dataUrlToBytes(dataUrl) {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ==================== Conversion ====================

// Standard page sizes in PDF points (72 pt/inch)
const PAGE_SIZES = {
  a4: { w: 595.28, h: 841.89 },
  letter: { w: 612, h: 792 },
};

/**
 * Convert images to a PDF. Each image becomes one page.
 *
 * @param {Array<{ dataUrl: string, width: number, height: number }>} images
 *   dataUrl is a PNG or JPEG data URL. width/height are the natural image dimensions.
 * @param {{ pageSize: 'a4'|'letter'|'fit', orientation: 'portrait'|'landscape'|'auto', margin: 'none'|'small'|'normal' }} options
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>}
 */
export async function imagesToPDF(images, options, onProgress = () => {}) {
  const doc = await PDFDocument.create();
  const margins = { none: 0, small: 20, normal: 40 };
  const margin = margins[options.margin] || 0;

  for (let i = 0; i < images.length; i++) {
    const img = images[i];

    // Embed image (convert to PNG bytes from data URL)
    const imgBytes = dataUrlToBytes(img.dataUrl);
    const isPng = img.dataUrl.includes('image/png');
    const embedded = isPng
      ? await doc.embedPng(imgBytes)
      : await doc.embedJpg(imgBytes);

    let pageW, pageH;

    if (options.pageSize === 'fit') {
      // Page matches image dimensions (at 72 DPI)
      pageW = img.width;
      pageH = img.height;
      const page = doc.addPage([pageW, pageH]);
      page.drawImage(embedded, { x: 0, y: 0, width: pageW, height: pageH });
    } else {
      // Fixed page size with image centered
      const size = PAGE_SIZES[options.pageSize];
      const isLandscape =
        options.orientation === 'landscape' ||
        (options.orientation === 'auto' && img.width > img.height);

      pageW = isLandscape ? size.h : size.w;
      pageH = isLandscape ? size.w : size.h;

      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;
      const scale = Math.min(availW / img.width, availH / img.height, 1);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      const x = (pageW - drawW) / 2;
      const y = (pageH - drawH) / 2;

      const page = doc.addPage([pageW, pageH]);
      page.drawImage(embedded, { x, y, width: drawW, height: drawH });
    }

    onProgress((i + 1) / images.length);
  }

  return doc.save({ useObjectStreams: true });
}

/**
 * Format file size in human-readable form.
 */
/**
 * Fill form fields in a PDF and optionally flatten them.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {Array<{ name: string, type: string, value: any, exportValue?: string }>} fieldData
 * @param {boolean} flatten — if true, bake values into the PDF (non-editable)
 * @returns {Promise<Uint8Array>}
 */
export async function fillAndSavePDF(arrayBuffer, fieldData, flatten) {
  return saveAllChanges(arrayBuffer, { fieldData, flatten });
}

/**
 * Save a PDF with annotations drawn onto the pages.
 *
 * Annotations are in page-coordinate space (top-left origin, scale=1).
 * This function converts Y to PDF space (bottom-left origin) before drawing.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {Array} annotations — annotation objects from useAnnotations
 * @returns {Promise<Uint8Array>}
 */
export async function saveWithAnnotations(arrayBuffer, annotations) {
  const doc = await PDFDocument.load(arrayBuffer);
  const pages = doc.getPages();
  const font = await doc.embedFont(StandardFonts.Helvetica);

  for (const a of annotations) {
    const page = pages[a.pageNum - 1];
    if (!page) continue;
    const { height: ph } = page.getSize();

    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;
      return rgb(r, g, b);
    };

    const c = hexToRgb(a.color);
    const op = a.opacity ?? 1;

    try {
      if (a.type === 'highlight') {
        page.drawRectangle({
          x: a.x,
          y: ph - a.y - Math.abs(a.h),
          width: Math.abs(a.w),
          height: Math.abs(a.h),
          color: c,
          opacity: op,
        });
      } else if (a.type === 'underline') {
        const ly = ph - (a.y + Math.abs(a.h));
        page.drawLine({
          start: { x: a.x, y: ly },
          end: { x: a.x + Math.abs(a.w), y: ly },
          color: c,
          thickness: 1.5,
          opacity: op,
        });
      } else if (a.type === 'strikethrough') {
        const ly = ph - (a.y + Math.abs(a.h) / 2);
        page.drawLine({
          start: { x: a.x, y: ly },
          end: { x: a.x + Math.abs(a.w), y: ly },
          color: c,
          thickness: 1.5,
          opacity: op,
        });
      } else if (a.type === 'draw' && a.points?.length > 1) {
        for (let i = 1; i < a.points.length; i++) {
          page.drawLine({
            start: { x: a.points[i - 1].x, y: ph - a.points[i - 1].y },
            end: { x: a.points[i].x, y: ph - a.points[i].y },
            color: c,
            thickness: a.strokeWidth || 2,
            opacity: op,
          });
        }
      } else if (a.type === 'text') {
        page.drawText(a.text, {
          x: a.x + 2,
          y: ph - a.y - a.fontSize,
          size: a.fontSize,
          font,
          color: c,
        });
      } else if (a.type === 'rectangle') {
        page.drawRectangle({
          x: Math.min(a.x, a.x + a.w),
          y: ph - Math.max(a.y, a.y + a.h),
          width: Math.abs(a.w),
          height: Math.abs(a.h),
          borderColor: c,
          borderWidth: a.strokeWidth || 2,
          opacity: op,
        });
      } else if (a.type === 'ellipse') {
        page.drawEllipse({
          x: a.x + a.w / 2,
          y: ph - (a.y + a.h / 2),
          xScale: Math.abs(a.w / 2),
          yScale: Math.abs(a.h / 2),
          borderColor: c,
          borderWidth: a.strokeWidth || 2,
          opacity: op,
        });
      } else if (a.type === 'line') {
        page.drawLine({
          start: { x: a.x1, y: ph - a.y1 },
          end: { x: a.x2, y: ph - a.y2 },
          color: c,
          thickness: a.strokeWidth || 2,
          opacity: op,
        });
      }
    } catch (_) {
      // Skip annotations that fail to render
    }
  }

  return doc.save({ useObjectStreams: true });
}

/**
 * Render a text edit (with segments) onto a pdf-lib page.
 * Each segment is drawn with its own font, size, color, and decorations.
 */
function renderTextEdit(page, edit, fonts, pageHeight) {
  const segments = editToSegments(edit);
  const s = edit.styles || {};
  // Keep at 0 so the PDF render matches the editor's on-screen overlay,
  // whose ContentEditableText container also uses padding: 0. Any non-zero
  // value here shifts the baked glyphs down by that amount, so a text
  // overlay the user placed JUST above a form underline in the editor ends
  // up sitting ON the underline after finalizeWrite reloads with the saved
  // bytes — making the line look "broken" everywhere they typed.
  const padding = 0;
  const lineHeightMultiplier = s.lineHeight || 1.3;
  const ph = pageHeight;

  const fullText = segments.map((seg) => seg.text).join('');
  const lines = fullText.split('\n');

  // Build a char→styles index for quick lookup
  const charStyles = [];
  for (const seg of segments) {
    for (let i = 0; i < seg.text.length; i++) {
      charStyles.push(seg.styles);
    }
  }

  const hexToRgb = (hex) => rgb(
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  );

  // Draw background if set
  if (s.backgroundColor && s.backgroundColor !== 'transparent') {
    const defaultFontSize = segments[0]?.styles?.fontSize || 14;
    const defaultLineHeight = defaultFontSize * lineHeightMultiplier;
    const totalHeight = lines.filter((l) => l).length * defaultLineHeight + padding * 2;
    page.drawRectangle({
      x: edit.x,
      y: ph - edit.y - totalHeight,
      width: edit.width,
      height: totalHeight,
      color: hexToRgb(s.backgroundColor),
    });
  }

  // Render each line, then each run within the line
  let charOffset = 0;
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    if (!line) {
      charOffset += 1; // for the '\n'
      continue;
    }

    // Break line into runs of consecutive chars with the same rendered styles
    const runs = [];
    let runStart = 0;
    for (let ci = 1; ci <= line.length; ci++) {
      const prevIdx = charOffset + ci - 1;
      const currIdx = charOffset + ci;
      if (ci === line.length || !stylesMatchForRender(charStyles[prevIdx], charStyles[currIdx])) {
        const runStyles = charStyles[charOffset + runStart] || segments[0]?.styles || {};
        runs.push({ text: line.slice(runStart, ci), styles: runStyles });
        runStart = ci;
      }
    }

    // Determine line baseline from the tallest font size in this line
    const maxFontSize = Math.max(...runs.map((r) => r.styles.fontSize || 14));
    const lineHeight = maxFontSize * lineHeightMultiplier;
    const baselineY = ph - (edit.y + padding + maxFontSize + li * lineHeight);

    // Draw each run
    let xCursor = edit.x + padding;
    for (const run of runs) {
      const fontSize = run.styles.fontSize || 14;
      const fontKey = resolveFontKey(run.styles);
      const font = fonts[fontKey] || fonts['Helvetica'];
      const color = hexToRgb(run.styles.color || '#000000');

      page.drawText(run.text, { x: xCursor, y: baselineY, size: fontSize, font, color });

      const textWidth = font.widthOfTextAtSize(run.text, fontSize);

      if (run.styles.underline) {
        page.drawLine({
          start: { x: xCursor, y: baselineY - fontSize * 0.15 },
          end: { x: xCursor + textWidth, y: baselineY - fontSize * 0.15 },
          color,
          thickness: Math.max(0.5, fontSize * 0.05),
        });
      }
      if (run.styles.strikethrough) {
        page.drawLine({
          start: { x: xCursor, y: baselineY + fontSize * 0.3 },
          end: { x: xCursor + textWidth, y: baselineY + fontSize * 0.3 },
          color,
          thickness: Math.max(0.5, fontSize * 0.05),
        });
      }

      xCursor += textWidth;
    }

    charOffset += line.length + 1; // +1 for the '\n'
  }
}

/** Check if two style objects produce the same rendered output. */
function stylesMatchForRender(a, b) {
  if (!a || !b) return false;
  return a.fontFamily === b.fontFamily &&
    a.fontSize === b.fontSize &&
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.strikethrough === b.strikethrough &&
    a.color === b.color;
}

/**
 * Save a PDF with text and image edits drawn onto the pages.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {Array} edits — edit objects from useEdits
 * @returns {Promise<Uint8Array>}
 */
export async function saveWithEdits(arrayBuffer, edits) {
  return saveAllChanges(arrayBuffer, { edits, annotations: [], flatten: false });
}

// ---- Universal flatten: paint any /AP /N annot into the page content ----
//
// On flatten we bake every visible overlay into the content stream and leave
// no editable handles behind. In-memory Scrivar edits/annotations are baked by
// the renderTextEdit / annotation-draw passes above. But a file can ALSO carry
// /AP appearance streams with no in-memory counterpart — values written by a
// prior Scrivar save whose restore banner was dismissed, or by an entirely
// different app (SmallPDF, Adobe). Those won't be touched by the draw passes,
// so on flatten we walk every page's /Annots and paint any annot carrying an
// /AP /N Form XObject straight into the content stream, then strip the annot.
//
// Preserve each stored annotation appearance and fail if it cannot be baked.
function flattenApAnnots(doc, supersededIds) {
  return flattenAppearances(doc, { supersededIds });
}

/**
 * PDF.js names each annotation after its object reference — "21R" for object
 * 21 generation 0 — and useFormFields carries that through as `annotId`. It is
 * the only trustworthy identity for a widget: field names are not unique
 * (20 checkboxes can share one name) and PDF.js half-decodes exportValue
 * (`#20` becomes a space, `#2f` stays escaped), so neither survives a
 * round-trip. Rebuild the same string from a pdf-lib ref to match them up.
 */
function annotIdOf(ref) {
  return ref.generationNumber === 0
    ? `${ref.objectNumber}R`
    : `${ref.objectNumber}R${ref.generationNumber}`;
}

/** Every /Widget annotation on every page, paired with its object ref. */
function* eachPageWidget(doc) {
  const ctx = doc.context;
  for (const page of doc.getPages()) {
    const annots = ctx.lookup(page.node.get(PDFName.of('Annots')));
    if (!(annots instanceof PDFArray)) continue;
    for (const ref of annots.asArray()) {
      if (!(ref instanceof PDFRef)) continue;
      const dict = ctx.lookup(ref);
      if (!(dict instanceof PDFDict)) continue;
      if (dict.get(PDFName.of('Subtype'))?.toString() !== '/Widget') continue;
      yield { ref, dict };
    }
  }
}

/** Walk /Parent to the root field, collecting whether any level declares /FT. */
function rootFieldOf(ctx, widgetRef, widgetDict) {
  let ref = widgetRef;
  let dict = widgetDict;
  let hasFieldType = !!dict.get(PDFName.of('FT'));
  const seen = new Set([annotIdOf(ref)]);
  for (;;) {
    const parentRef = dict.get(PDFName.of('Parent'));
    if (!(parentRef instanceof PDFRef) || seen.has(annotIdOf(parentRef))) break;
    const parent = ctx.lookup(parentRef);
    if (!(parent instanceof PDFDict)) break;
    seen.add(annotIdOf(parentRef));
    ref = parentRef;
    dict = parent;
    hasFieldType = hasFieldType || !!dict.get(PDFName.of('FT'));
  }
  return { ref, dict, hasFieldType };
}

/**
 * Register every page /Widget with the AcroForm so pdf-lib can address it.
 *
 * Plenty of real forms leave /AcroForm /Fields listing only a fraction of the
 * widgets actually drawn on the pages — the Streamlined Sales Tax exemption
 * certificate ships 128 widgets and lists 15. useFormFields detects fields
 * from the PAGE annotations, so the UI happily renders and accepts input for
 * all 128, but the fill loop below walks pdf-lib's form.getFields(), which can
 * only reach what /Fields reaches. The other 113 were never visited and
 * everything typed into them was dropped without a warning. Adopting the
 * strays first makes the whole form addressable.
 *
 * Widgets are adopted at their ROOT field so kid groups stay intact rather
 * than each kid being promoted to a top-level field.
 *
 * @returns {number} how many root fields were added to /Fields
 */
function adoptOrphanWidgets(doc) {
  const ctx = doc.context;
  const acroRef = doc.catalog.get(PDFName.of('AcroForm'));
  const acro = acroRef ? ctx.lookup(acroRef) : null;
  if (!(acro instanceof PDFDict)) return 0;

  let fields = ctx.lookup(acro.get(PDFName.of('Fields')));
  if (!(fields instanceof PDFArray)) {
    fields = ctx.obj([]);
    acro.set(PDFName.of('Fields'), fields);
  }

  const known = new Set();
  for (const ref of fields.asArray()) {
    if (ref instanceof PDFRef) known.add(annotIdOf(ref));
  }

  let adopted = 0;
  for (const { ref, dict } of eachPageWidget(doc)) {
    const root = rootFieldOf(ctx, ref, dict);
    // No /FT anywhere in the chain means this isn't a form field at all;
    // adopting it would only hand pdf-lib something it can't classify.
    if (!root.hasFieldType) continue;
    const key = annotIdOf(root.ref);
    if (known.has(key)) continue;
    known.add(key);
    fields.push(root.ref);
    adopted++;
  }
  return adopted;
}

/** The widget's "on" appearance state — the /AP /N key that isn't /Off. */
function onStateNameOf(ctx, widgetDict) {
  const ap = ctx.lookup(widgetDict.get(PDFName.of('AP')));
  if (!(ap instanceof PDFDict)) return null;
  const normal = ctx.lookup(ap.get(PDFName.of('N')));
  if (!(normal instanceof PDFDict)) return null;
  // Return the PDFName itself, never a decoded string — re-encoding a name
  // like `Accomodation#2fFood#20Services` would not round-trip byte-for-byte.
  for (const key of normal.keys()) {
    if (key.toString() !== '/Off') return key;
  }
  return null;
}

/**
 * Set /Btn values one widget at a time for fields whose kids share a name.
 *
 * Section 3 of the exemption certificate is a single /Btn field with 20 kid
 * widgets, each carrying its own on-state ("Retail trade", "Manufacturing"…).
 * The name-keyed map in the fill loop can only hold one entry per name, so all
 * 20 collapsed to whichever came last and ticking any other box was thrown
 * away. Here each widget is matched by annotId instead, the owning field's /V
 * is set to the ticked widget's on-state, and every kid's /AS is set to match.
 *
 * @returns {Set<string>} annotIds of the fields handled here, so the
 *   name-keyed loop can skip them instead of overwriting this work.
 */
function applyGroupedButtonValues(doc, widgetData) {
  const handled = new Set();
  const byAnnotId = new Map();
  for (const fd of widgetData) {
    if (fd.annotId) byAnnotId.set(fd.annotId, fd);
  }
  if (byAnnotId.size === 0) return handled;

  const ctx = doc.context;
  const groups = new Map();
  for (const { ref, dict } of eachPageWidget(doc)) {
    const fd = byAnnotId.get(annotIdOf(ref));
    if (!fd) continue;
    const root = rootFieldOf(ctx, ref, dict);
    if (root.dict.get(PDFName.of('FT'))?.toString() !== '/Btn') continue;
    const kids = ctx.lookup(root.dict.get(PDFName.of('Kids')));
    // A lone widget is unambiguous by name — leave it to the normal loop so
    // pdf-lib keeps regenerating its appearance the way it always has.
    if (!(kids instanceof PDFArray) || kids.size() < 2) continue;
    const key = annotIdOf(root.ref);
    if (!groups.has(key)) groups.set(key, { fieldDict: root.dict, kids, ticked: null });
    if (fd.value) groups.get(key).ticked = ref;
  }

  for (const [key, { fieldDict, kids, ticked }] of groups) {
    const tickedDict = ticked ? ctx.lookup(ticked) : null;
    const onState = tickedDict ? onStateNameOf(ctx, tickedDict) : null;
    if (ticked && !onState) continue; // no appearance to switch to — leave it alone

    fieldDict.set(PDFName.of('V'), onState || PDFName.of('Off'));
    for (const kidRef of kids.asArray()) {
      if (!(kidRef instanceof PDFRef)) continue;
      const kid = ctx.lookup(kidRef);
      if (!(kid instanceof PDFDict)) continue;
      const isTicked = ticked && annotIdOf(kidRef) === annotIdOf(ticked);
      kid.set(PDFName.of('AS'), isTicked ? onState : PDFName.of('Off'));
    }
    handled.add(key);
  }
  return handled;
}

/**
 * Collapse each widget's /AP /N to the single stream for its current /AS.
 *
 * pdf-lib's `findWidgetAppearanceRef` only picks a checkbox/radio on-state out
 * of /AP /N when /N is a DIRECT dictionary. `getNormalAppearance()` returns an
 * unresolved PDFRef when /N is stored indirectly — which is how plenty of real
 * forms store it — so the `instanceof PDFDict` branch never runs and flatten()
 * registers the STATE DICTIONARY itself as a Form XObject. A dict is not a
 * stream, so it draws nothing: on the exemption certificate every checkbox in
 * sections 3 and 4 vanished from the flattened page, squares and all, taking
 * the user's selection with it ("no XObject subtype specified" × 68).
 *
 * Resolving /N to the concrete state stream up front means pdf-lib sees a real
 * appearance ref whichever branch it takes. Only safe to do when flattening —
 * it strips the widget's other states.
 *
 * @returns {number} how many widgets were collapsed
 */
function resolveButtonAppearances(doc) {
  const ctx = doc.context;
  let resolved = 0;
  for (const { dict } of eachPageWidget(doc)) {
    const ap = ctx.lookup(dict.get(PDFName.of('AP')));
    if (!(ap instanceof PDFDict)) continue;
    const normal = ctx.lookup(ap.get(PDFName.of('N')));
    // A stream is already a usable appearance; only a state dict needs picking.
    if (!(normal instanceof PDFDict)) continue;

    const state = dict.get(PDFName.of('AS'));
    let chosen = state ? normal.get(state) : undefined;
    if (!chosen) chosen = normal.get(PDFName.of('Off'));
    if (!chosen) {
      const [first] = normal.keys();
      chosen = first ? normal.get(first) : undefined;
    }
    if (!chosen) continue;

    ap.set(PDFName.of('N'), chosen);
    resolved++;
  }
  return resolved;
}

/**
 * Drop page /Annots entries whose object no longer exists.
 *
 * pdf-lib's `removeField` hands the APPEARANCE ref to `page.node.removeAnnot`
 * and then removes `field.ref`, which only clears /Annots when the field and
 * its widget are the same merged object. A field with /Kids leaves every kid
 * ref in /Annots and then deletes the kid objects — so a flattened page points
 * at dead objects (33 of them on the exemption certificate, one per checkbox).
 *
 * @returns {number} how many dead refs were removed
 */
function pruneDanglingAnnots(doc) {
  const ctx = doc.context;
  const live = new Set();
  for (const [ref] of ctx.enumerateIndirectObjects()) live.add(ref.tag);

  let pruned = 0;
  for (const page of doc.getPages()) {
    const annots = ctx.lookup(page.node.get(PDFName.of('Annots')));
    if (!(annots instanceof PDFArray)) continue;
    for (let i = annots.size() - 1; i >= 0; i--) {
      const ref = annots.get(i);
      if (ref instanceof PDFRef && !live.has(ref.tag)) {
        annots.remove(i);
        pruned++;
      }
    }
  }
  return pruned;
}

/**
 * Unified save — applies form fills, edits, and annotations in one pass.
 * This ensures all changes are saved together to a single output PDF.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {{ fieldData?: Array, flatten?: boolean, annotations?: Array, edits?: Array }} options
 * @returns {Promise<Uint8Array>}
 */
export async function saveAllChanges(arrayBuffer, { fieldData, flatten, annotations, edits, preserveOverlays = true }) {
  // Widget IDs identify PDF objects. Apply their values before the content
  // engine rewrites object numbers, especially shared-name radio/checkbox kids.
  if (fieldData?.length && removalTargets(edits).length) {
    arrayBuffer = await saveAllChanges(arrayBuffer, { fieldData });
    fieldData = undefined;
  }
  const prepared = await prepareTextRemoval(arrayBuffer, edits || []);
  arrayBuffer = prepared.bytes;
  edits = prepared.edits;
  // Flatten the exact canonical appearances, instead of laying out text a
  // second time with a renderer that has different wrapping/font metrics.
  if (flatten && (edits?.length || annotations?.length) && ![...(edits || []), ...(annotations || [])].some((item) => item.bakedBounds && !item.apCarried)) {
    const canonical = await saveAllChanges(arrayBuffer, { fieldData, edits, annotations, preserveOverlays: true, flatten: false });
    return saveAllChanges(canonical, { flatten: true, preserveOverlays: false });
  }

  let doc = await PDFDocument.load(arrayBuffer);
  const pages = doc.getPages();

  // 1. Apply form field values
  if (hasDigitalSignature(doc)) {
    if (flatten || fieldData?.length || edits?.length || annotations?.length) throw new Error('This document contains a digital signature. Open an unsigned copy before changing it.');
    return new Uint8Array(arrayBuffer);
  }
  if (fieldData && fieldData.length > 0) {
    // Split widget-backed fields (real AcroForm fields, edited via pdf-lib)
    // from freetext-backed fields (visual placeholders detected by
    // useFormFields when a "fillable PDF" was authored with FreeText
    // annotations instead of /Widget form fields).
    const widgetData = fieldData.filter((fd) => (fd.source || 'widget') === 'widget');
    const freetextData = fieldData.filter((fd) => fd.source === 'freetext');

    if (widgetData.length > 0) {
      let form;
      try { form = doc.getForm(); } catch (_) {}
      if (form) {
        // Reach the widgets /AcroForm /Fields forgot to list, then settle the
        // /Btn groups whose kids share one field name. Both have to happen
        // before the name-keyed loop: the first makes those fields visible to
        // form.getFields(), the second claims the ones that loop would mangle.
        try { adoptOrphanWidgets(doc); } catch (_) {}
        let groupedFields = new Set();
        try { groupedFields = applyGroupedButtonValues(doc, widgetData); } catch (_) {}

        const valueMap = new Map();
        for (const fd of widgetData) valueMap.set(fd.name, fd);

        const allFields = form.getFields();
        for (const field of allFields) {
          if (groupedFields.has(annotIdOf(field.acroField.ref))) continue;
          const name = field.getName();
          let fd = valueMap.get(name);
          if (!fd) {
            for (const [jsName, data] of valueMap) {
              if (jsName.endsWith(name) || name.endsWith(jsName) || jsName.includes(name)) {
                fd = data; break;
              }
            }
          }
          if (!fd) continue;
          try {
            if (field instanceof PDFTextField) field.setText(String(fd.value || ''));
            else if (field instanceof PDFCheckBox) { if (fd.value) field.check(); else field.uncheck(); }
            else if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
              if (fd.value == null || fd.value === '' || Array.isArray(fd.value) && !fd.value.length) field.clear();
              else field.select(fd.value);
            }
            else if (field instanceof PDFRadioGroup) {
              if (fd.value && fd.exportValue) field.select(fd.exportValue);
              else field.clear();
            }
          } catch (_) {}
        }
        if (flatten) {
          // These three work around pdf-lib bugs that only bite fields with
          // /Kids — see each helper. Without them Save as Filled silently drops
          // every grouped checkbox and leaves the page pointing at dead objects.
          //
          // Order matters. flatten() regenerates appearances first by default,
          // so run that ourselves BEFORE collapsing /AP /N — a collapsed /N is
          // a stream where PDFCheckBox.needsAppearancesUpdate expects a state
          // dict, so it would report "needs update" and overwrite the state we
          // just picked with a default one (the ticked box came out blank).
          // Text fields still need that pass to render what setText wrote.
          try { form.updateFieldAppearances(); } catch (_) {}
          try { resolveButtonAppearances(doc); } catch (_) {}
          try { form.flatten({ updateFieldAppearances: false }); } catch (_) {}
          try { pruneDanglingAnnots(doc); } catch (_) {}
        } else {
          // pdf-lib regenerates each filled field's /AP on doc.save(), but
          // leaves a source /AcroForm /NeedAppearances flag untouched. Viewers
          // that honor it (Acrobat, Preview) then re-synthesize the value OVER
          // our stored /AP at slightly different metrics — the "shadow /
          // after-image" users reported after fill -> save -> reopen. Clear it
          // so the single stored /AP is authoritative. (On flatten the field is
          // baked into the page, so the flag is moot.)
          try {
            form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.False);
          } catch (_) {}
        }
      }
    }

    // FreeText-as-field values get drawn directly on the page at the annot's
    // rect — pdf-lib's form.getFields() won't see these so they need their
    // own paint pass. We always paint (flatten or not): the original FreeText
    // annotation is left in place underneath, and the painted text sits on
    // top. Viewers that respect annot z-order will show the new text; ones
    // that don't will still see the original (empty) FreeText box plus our
    // overlay, which renders correctly.
    if (freetextData.length > 0) {
      const helvetica = await doc.embedFont(StandardFonts.Helvetica);
      // Threshold below which a FreeText rect is treated as a "pointer
      // marker" rather than an input box. PDFs authored as fillable forms
      // commonly use tiny markers (~9 units = ~12px) that ALWAYS sit above
      // the label's writing line; the user is expected to type on the next
      // line down. Inputs that are TALL (≥ this) are treated as real input
      // boxes and get vertical-centered text. Picked 12 because the
      // observed marker pattern is ~9 units and a real input box is rarely
      // less than 14 units tall.
      const MARKER_RECT_MAX_H = 12;
      for (const fd of freetextData) {
        const value = String(fd.value || '').trim();
        if (!value) continue;
        const pageIdx = (fd.pageNum || 1) - 1;
        const page = pages[pageIdx];
        if (!page || !fd.rect || fd.rect.length < 4) continue;
        const [x1, y1, x2, y2] = fd.rect;
        const rectH = Math.abs(y2 - y1);
        // Slightly smaller than rect height to leave breathing room; cap so
        // very tall annots don't render with absurdly large text.
        const fontSize = Math.max(8, Math.min(rectH - 2, 14));

        // Calculate baseline y in PDF coords (y goes up).
        //   - Marker rects (< 12 units): the marker is a pointer to the
        //     line, not the input area. Place the baseline one font-size
        //     BELOW the marker bottom so the text lands on the writing
        //     line the user expects, not floating above the labels.
        //     Verified against the Wheel credit application: markers at
        //     y_bottom=335.5, labels at baseline=329.2 — drawing at
        //     bottom-fontSize=327.5 lands ~2 units below the label
        //     baseline, visually aligned.
        //   - Input-box rects (>= 12 units): vertically center within the
        //     rect, as before. Real input boxes are sized to enclose the
        //     intended text area, so centering is correct.
        const baselineY = rectH < MARKER_RECT_MAX_H
          ? Math.min(y1, y2) - fontSize
          : Math.min(y1, y2) + (rectH - fontSize) / 2;

        try {
          page.drawText(value, {
            x: Math.min(x1, x2) + 1,
            y: baselineY,
            size: fontSize,
            font: helvetica,
          });
        } catch (_) { /* malformed rect — skip */ }
      }
    }
  }

  // 2. Apply edits (text/images) — below annotations in visual order
  // Draw all edits visually: user-added edits need to be drawn the first
  // time, and detected/restored edits need a redraw if modified or deleted.
  // The sidecar + /Annot dicts are ADDITIONS to the flat draw — the file
  // must look correct in any viewer.
  if (edits && edits.length > 0) {
    // Need to draw when any edit needs rendering:
    // - Fresh user-added edits (not restored from sidecar) need a first draw
    // - Detected/restored edits only need a redraw if modified or deleted
    const needsDrawing = edits.some(e =>
      (e.source !== 'detected' && !e.restored) || e.deleted || e.modified
    );

    if (needsDrawing) {
      const fonts = {
        'Helvetica': await doc.embedFont(StandardFonts.Helvetica),
        'Helvetica-Bold': await doc.embedFont(StandardFonts.HelveticaBold),
        'Helvetica-Oblique': await doc.embedFont(StandardFonts.HelveticaOblique),
        'Helvetica-BoldOblique': await doc.embedFont(StandardFonts.HelveticaBoldOblique),
        'Times-Roman': await doc.embedFont(StandardFonts.TimesRoman),
        'Times-Bold': await doc.embedFont(StandardFonts.TimesRomanBold),
        'Times-Italic': await doc.embedFont(StandardFonts.TimesRomanItalic),
        'Times-BoldItalic': await doc.embedFont(StandardFonts.TimesRomanBoldItalic),
        'Courier': await doc.embedFont(StandardFonts.Courier),
        'Courier-Bold': await doc.embedFont(StandardFonts.CourierBold),
        'Courier-Oblique': await doc.embedFont(StandardFonts.CourierOblique),
        'Courier-BoldOblique': await doc.embedFont(StandardFonts.CourierBoldOblique),
      };

      for (const edit of edits) {
        const page = pages[edit.pageNum - 1];
        if (!page) continue;
        const { height: ph } = page.getSize();
        try {
          // Source glyphs have already been removed. Text uses canonical
          // annotation appearances; only legacy flatten needs a draw pass.
          if (edit.type === 'text' && !flatten) continue;

          // Rotated text is locked (fontSize/position math here is horizontal-only,
          // so writing it would corrupt the block) — never write it, whited-out or otherwise.
          if (edit.rotationLocked) continue;

          // Unchanged detected content: the source pixels are already on the
          // page — don't touch them.
          if (edit.source === 'detected' && !edit.deleted && !edit.modified) continue;

          // Unchanged restored overlay: the flat-draw from a prior save is
          // already on the page at bakedBounds. Re-drawing here stacks a
          // duplicate on top (visible as bolded text / doubled image) and
          // bloats the content stream on every round-trip. Skip it.
          if (edit.restored && !edit.deleted && !edit.modified) continue;

          // White-out original bounds for modified or deleted detected content
          if (edit.type !== 'text' && edit.source === 'detected' && edit.originalBounds) {
            const ob = edit.originalBounds;
            page.drawRectangle({
              x: ob.x,
              y: ph - ob.y - ob.height,
              width: ob.width,
              height: ob.height,
              color: rgb(1, 1, 1),
              borderWidth: 0,
            });
          }

          // Old image/signature saves baked their pixels into the page.
          // Text never uses this mask: prepareTextRemoval rewrites its source.
          if (edit.type !== 'text' && edit.bakedBounds) {
            const bb = edit.bakedBounds;
            page.drawRectangle({ x: bb.x, y: ph - bb.y - bb.height, width: bb.width, height: bb.height,
              color: rgb(1, 1, 1), borderWidth: 0 });
          }

          // Skip drawing content for deleted items
          if (edit.deleted) continue;

          if (edit.type === 'text' && (edit.content || edit.segments)) {
            renderTextEdit(page, edit, fonts, ph);
          } else if ((edit.type === 'image' || edit.type === 'signature') && edit.content
                     && (flatten || edit.content.length > 2_000_000)) {
            // Small images carry via the /Stamp /AP on normal saves (mirrors
            // IMAGE_AP_MAX_CHARS in pdfAnnotations.js); large images keep baking
            // because the sidecar strips their content and an /AP couldn't be
            // restored on reopen. On flatten everything bakes.
            const imgBytes = dataUrlToBytes(edit.content);
            const image = edit.content.includes('image/png') ? await doc.embedPng(imgBytes) : await doc.embedJpg(imgBytes);
            // Match editor's object-contain: fit image within the bounding box
            // preserving native aspect ratio, centered. Prevents stretching when
            // the user resizes the box to a ratio different from the image's.
            const boxW = edit.width;
            const boxH = edit.height;
            const imgW = image.width || boxW;
            const imgH = image.height || boxH;
            const scale = Math.min(boxW / imgW, boxH / imgH);
            const drawW = imgW * scale;
            const drawH = imgH * scale;
            const offsetX = (boxW - drawW) / 2;
            const offsetY = (boxH - drawH) / 2;
            page.drawImage(image, {
              x: edit.x + offsetX,
              y: ph - edit.y - edit.height + offsetY,
              width: drawW,
              height: drawH,
            });
          }
        } catch (_) {}
      }
    }
  }

  // 3. Apply annotations — on top of everything
  // Draw annotations visually — the sidecar preserves editability on reopen,
  // the flat draw ensures recipients/other viewers see them correctly.
  if (annotations && annotations.length > 0) {
    const font = await doc.embedFont(StandardFonts.Helvetica);
    for (const a of annotations) {
      const page = pages[a.pageNum - 1];
      if (!page) continue;
      const { height: ph } = page.getSize();
      const hexToRgb = (hex) => rgb(parseInt(hex.slice(1,3),16)/255, parseInt(hex.slice(3,5),16)/255, parseInt(hex.slice(5,7),16)/255);
      const op = a.opacity ?? 1;
      try {
        // Non-text annotations carry via /AP on normal saves; bake only on
        // flatten (flattenApAnnots paints the /AP). Mirrors the text gate above.
        if (!flatten) continue;

        // Unchanged restored annotation: the flat-draw from a prior save is
        // already on the page at bakedBounds. Re-drawing stacks a duplicate
        // (visible as darkened strokes) and bloats the content stream each
        // round-trip. Skip it.
        if (a.restored && !a.modified && !a.deleted) continue;

        // Whiteout prior baked pixels when modifying or deleting a restored
        // annotation. Without this, the old stroke ghosts through even after
        // the overlay is gone from the editor.
        if (a.bakedBounds) {
          const bb = a.bakedBounds;
          page.drawRectangle({
            x: bb.x,
            y: ph - bb.y - bb.height,
            width: bb.width,
            height: bb.height,
            color: rgb(1, 1, 1),
            borderWidth: 0,
          });
        }

        // Skip drawing content for deleted annotations — the whiteout above
        // is the only thing they contribute to the save.
        if (a.deleted) continue;

        const c = hexToRgb(a.color);
        if (a.type === 'highlight') page.drawRectangle({ x: a.x, y: ph - a.y - Math.abs(a.h), width: Math.abs(a.w), height: Math.abs(a.h), color: c, opacity: op });
        else if (a.type === 'underline') page.drawLine({ start: { x: a.x, y: ph - (a.y + Math.abs(a.h)) }, end: { x: a.x + Math.abs(a.w), y: ph - (a.y + Math.abs(a.h)) }, color: c, thickness: 1.5, opacity: op });
        else if (a.type === 'strikethrough') page.drawLine({ start: { x: a.x, y: ph - (a.y + Math.abs(a.h)/2) }, end: { x: a.x + Math.abs(a.w), y: ph - (a.y + Math.abs(a.h)/2) }, color: c, thickness: 1.5, opacity: op });
        else if (a.type === 'draw' && a.points?.length > 1) { for (let i = 1; i < a.points.length; i++) page.drawLine({ start: { x: a.points[i-1].x, y: ph - a.points[i-1].y }, end: { x: a.points[i].x, y: ph - a.points[i].y }, color: c, thickness: a.strokeWidth || 2, opacity: op }); }
        else if (a.type === 'text') page.drawText(a.text, { x: a.x + 2, y: ph - a.y - a.fontSize, size: a.fontSize, font, color: c });
        else if (a.type === 'rectangle') page.drawRectangle({ x: Math.min(a.x, a.x+a.w), y: ph - Math.max(a.y, a.y+a.h), width: Math.abs(a.w), height: Math.abs(a.h), borderColor: c, borderWidth: a.strokeWidth || 2, opacity: op });
        else if (a.type === 'ellipse') page.drawEllipse({ x: a.x+a.w/2, y: ph-(a.y+a.h/2), xScale: Math.abs(a.w/2), yScale: Math.abs(a.h/2), borderColor: c, borderWidth: a.strokeWidth || 2, opacity: op });
        else if (a.type === 'line') page.drawLine({ start: { x: a.x1, y: ph - a.y1 }, end: { x: a.x2, y: ph - a.y2 }, color: c, thickness: a.strokeWidth || 2, opacity: op });
        else if (a.type === 'checkmark') {
          const nx = Math.min(a.x, a.x + a.w);
          const ny = a.y;
          const w = Math.abs(a.w), h = Math.abs(a.h);
          // Draw checkmark as two connected lines
          const x1 = nx + w * 0.15, y1 = ph - (ny + h * 0.55);
          const x2 = nx + w * 0.4, y2 = ph - (ny + h * 0.8);
          const x3 = nx + w * 0.85, y3 = ph - (ny + h * 0.2);
          page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, color: c, thickness: a.strokeWidth || 3, opacity: op });
          page.drawLine({ start: { x: x2, y: y2 }, end: { x: x3, y: y3 }, color: c, thickness: a.strokeWidth || 3, opacity: op });
        }
        else if (a.type === 'cross') {
          const nx = Math.min(a.x, a.x + a.w);
          const ny = a.y;
          const w = Math.abs(a.w), h = Math.abs(a.h);
          const pad = Math.min(w, h) * 0.15;
          page.drawLine({ start: { x: nx + pad, y: ph - (ny + pad) }, end: { x: nx + w - pad, y: ph - (ny + h - pad) }, color: c, thickness: a.strokeWidth || 3, opacity: op });
          page.drawLine({ start: { x: nx + w - pad, y: ph - (ny + pad) }, end: { x: nx + pad, y: ph - (ny + h - pad) }, color: c, thickness: a.strokeWidth || 3, opacity: op });
        }
        else if (a.type === 'arrow') {
          // Arrow shaft
          page.drawLine({ start: { x: a.x1, y: ph - a.y1 }, end: { x: a.x2, y: ph - a.y2 }, color: c, thickness: a.strokeWidth || 2, opacity: op });
          // Arrowhead
          const dx = a.x2 - a.x1, dy = a.y2 - a.y1;
          const angle = Math.atan2(dy, dx);
          const headLen = Math.min(15, Math.sqrt(dx * dx + dy * dy) * 0.3);
          const ax = a.x2 - headLen * Math.cos(angle - Math.PI / 6);
          const ay = ph - (a.y2 - headLen * Math.sin(angle - Math.PI / 6));
          const bx = a.x2 - headLen * Math.cos(angle + Math.PI / 6);
          const by = ph - (a.y2 - headLen * Math.sin(angle + Math.PI / 6));
          page.drawLine({ start: { x: ax, y: ay }, end: { x: a.x2, y: ph - a.y2 }, color: c, thickness: a.strokeWidth || 2, opacity: op });
          page.drawLine({ start: { x: bx, y: by }, end: { x: a.x2, y: ph - a.y2 }, color: c, thickness: a.strokeWidth || 2, opacity: op });
        }
      } catch (_) {}
    }
  }

  // 3b. Universal flatten — bake any /AP /N annot (foreign or prior-Scrivar)
  // into the content stream and strip it. Runs only on flatten, after the
  // draw passes above and before the persist tail. Scrivar annots whose
  // in-memory overlay was just redrawn (modified/deleted) are stripped without
  // painting so the value isn't drawn twice; everything else (external annots,
  // restored-unmodified Scrivar annots, Scrivar annots with no in-memory
  // counterpart) is painted exactly once.
  if (flatten) {
    // supersededIds match annots by id via scrivarIdFromAnnot, which reads the
    // annot /NM. This only works because makeBaseDict (pdfAnnotations.js) always
    // writes /NM === `scrivar:${edit.id}` — keep that invariant or stale /AP
    // double-paints. See scrivarIdFromAnnot.
    const supersededIds = new Set();
    for (const e of edits || []) {
      if ((e.modified || e.deleted) && e.id != null) supersededIds.add(String(e.id));
    }
    for (const a of annotations || []) {
      if ((a.modified || a.deleted) && a.id != null) supersededIds.add(String(a.id));
    }
    doc = await ensureFlattenAppearances(doc, { supersededIds });
    flattenApAnnots(doc, supersededIds);
  }

  // 4. Persist overlay metadata + standard PDF annotations (single chokepoint).
  await persistOverlaysToDoc(doc, edits || [], annotations || [], {
    preserveOverlays: preserveOverlays !== false,
  });

  if (flatten) removeUnreachableObjects(doc);
  return doc.save({ useObjectStreams: true, updateFieldAppearances: !flatten });
}

// ==================== Flatten ====================

/**
 * Analyze a PDF for interactive elements that can be flattened.
 *
 * Uses PDF.js to count form fields and annotations, and pdf-lib to check
 * for JavaScript in the document catalog.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {object} pdfjsLib
 * @returns {Promise<{ formFields: number, annotations: number, hasJS: boolean, isAlreadyFlat: boolean }>}
 */
export async function analyzePDF(arrayBuffer, pdfjsLib) {
  const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;

  let formFields = 0;
  let annotations = 0;

  for (let i = 1; i <= pdfDoc.numPages; i++) {
    const page = await pdfDoc.getPage(i);
    const annots = await page.getAnnotations();
    for (const annot of annots) {
      if (annot.subtype === 'Widget') {
        formFields++;
      } else if (annot.subtype !== 'Link') {
        annotations++;
      }
    }
  }
  pdfDoc.destroy();

  // Check for JavaScript via pdf-lib catalog inspection (best-effort)
  let hasJS = false;
  try {
    const doc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const catalog = doc.catalog;

    // Check OpenAction for JavaScript
    const openAction = catalog.get(PDFName.of('OpenAction'));
    if (openAction) {
      const actionDict = doc.context.lookup(openAction);
      if (actionDict) {
        const s = actionDict.get?.(PDFName.of('S'));
        if (s && s.toString() === '/JavaScript') hasJS = true;
      }
    }

    // Check Names → JavaScript name tree
    const names = catalog.get(PDFName.of('Names'));
    if (names) {
      const namesDict = doc.context.lookup(names);
      if (namesDict && namesDict.get?.(PDFName.of('JavaScript'))) hasJS = true;
    }

    // Check AA (Additional Actions) on catalog
    if (catalog.get(PDFName.of('AA'))) hasJS = true;
  } catch (_) {
    // Best-effort — leave hasJS false if pdf-lib can't parse
  }

  const isAlreadyFlat = formFields === 0 && annotations === 0 && !hasJS;
  return { formFields, annotations, hasJS, isAlreadyFlat };
}

/** Flatten selected interactive content without rasterizing the page. */
export async function flattenPDF(arrayBuffer, options, _pdfjsLib, onProgress = () => {}) {
  const { flattenForms, flattenAnnotations, removeJS, reduceFileSize } = options;
  if (!flattenForms && !flattenAnnotations && !removeJS && !reduceFileSize) {
    return { bytes: new Uint8Array(arrayBuffer), changed: false, rasterized: false };
  }
  let doc = await PDFDocument.load(arrayBuffer);
  if (hasDigitalSignature(doc)) throw new Error('This document contains a digital signature. Open an unsigned copy before flattening.');
  doc = await ensureFlattenAppearances(doc, { forms: flattenForms, annotations: flattenAnnotations });
  onProgress(0.05);
  let changed = flattenAppearances(doc, { forms: flattenForms, annotations: flattenAnnotations }) > 0;
  if (removeJS) {
    const ctx = doc.context;
    const visited = new Set();
    const cleanAction = value => {
      const action = ctx.lookup(value);
      if (action instanceof PDFDict) {
        if (action.get(PDFName.of('S'))?.toString() === '/JavaScript') { changed = true; return undefined; }
        if (visited.has(action)) return value;
        visited.add(action);
        const next = action.get(PDFName.of('Next'));
        if (next) { const cleaned = cleanAction(next); if (cleaned) action.set(PDFName.of('Next'), cleaned); else action.delete(PDFName.of('Next')); }
      } else if (action instanceof PDFArray) {
        if (visited.has(action)) return value;
        visited.add(action);
        for (let i = action.size() - 1; i >= 0; i--) if (!cleanAction(action.get(i))) action.remove(i);
        if (!action.size()) return undefined;
      }
      return value;
    };
    for (const [, object] of ctx.enumerateIndirectObjects()) {
      const dict = object instanceof PDFDict ? object : object.dict;
      if (!(dict instanceof PDFDict)) continue;
      for (const name of ['OpenAction', 'A']) {
        const value = dict.get(PDFName.of(name));
        if (value && !cleanAction(value)) dict.delete(PDFName.of(name));
      }
      const aa = dict.lookupMaybe(PDFName.of('AA'), PDFDict);
      if (aa) {
        for (const [name, action] of aa.entries()) if (!cleanAction(action)) aa.delete(name);
        if (!aa.keys().length) dict.delete(PDFName.of('AA'));
      }
    }
    const names = doc.catalog.lookupMaybe(PDFName.of('Names'), PDFDict);
    if (names?.delete(PDFName.of('JavaScript'))) changed = true;
  }
  onProgress(0.8);
  if (flattenAnnotations) await persistOverlaysToDoc(doc, [], [], { preserveOverlays: false });
  removeUnreachableObjects(doc);
  const output = await doc.save({ useObjectStreams: true, updateFieldAppearances: false });
  onProgress(1);
  return {
    bytes: changed || reduceFileSize && output.length < arrayBuffer.byteLength ? output : new Uint8Array(arrayBuffer),
    changed: changed || reduceFileSize && output.length < arrayBuffer.byteLength,
    rasterized: false,
  };
}

/**
 * Remove marked text, image pixels, overlapping vector objects and annotations
 * locally, then perform a full garbage-collected rewrite. Other page content
 * remains native PDF content; the page is never replaced by a screenshot.
 *
 * @param {ArrayBuffer} arrayBuffer — The source PDF bytes.
 * @param {{ id: string, pageNumber: number, x: number, y: number, width: number, height: number }[]} marks
 *   Redaction marks in PDF coordinate space (origin = bottom-left of page).
 * @param {{ r: number, g: number, b: number }} fillColor — RGB 0-255 for redaction box fill.
 * @param {object} pdfjsLib — PDF.js library reference.
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Uint8Array>} The redacted PDF bytes.
 */
export async function redactPDF(arrayBuffer, marks, fillColor, _pdfjsLib, onProgress = () => {}) {
  return removePdfContent(arrayBuffer, marks, { redact: true, fillColor, onProgress });
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
